/* Live search. Enhances every [data-predictive] wrapper (header desktop and
   mobile forms, hero "Find a part", search band) with a dropdown of matching
   parts: the query goes to the Predictive Search API with section_id, and
   sections/stoklink-predictive-search.liquid renders the rows server-side.

   Wrapper contract (docs/tasks/phase-5.md):
     <div class="predictive" data-predictive
          data-predictive-url="/search/suggest" data-fallback-url="/search"
          data-section="stoklink-predictive-search" data-limit="6"
          data-unavailable="last" data-types="product"
          [data-predictive-loading="Searching…"]>
       <form role="search"> <input type="search" name="q" id="…"> … </form>
       <div class="predictive__results" hidden data-predictive-results></div>
       <p class="visually-hidden" aria-live="polite" data-predictive-live></p>
     </div>

   - 2+ characters, 300 ms debounce, one request in flight (AbortController),
     the last 20 answers cached per wrapper.
   - 417 (buyer locale without predictive search): the wrapper switches to
     the search page fallback (?section_id=…&type=product) for good.
   - 429: honours Retry-After and pauses. Any other error closes quietly; the
     form still submits as a normal search.
   - Combobox keyboard: ArrowDown/ArrowUp move aria-activedescendant across
     every [role=option], Enter opens the active option (else submits),
     Escape closes and keeps focus, Tab / outside click / focus leaving the
     wrapper closes. [data-predictive-submit] submits the form.
   - Events: dispatches `stoklink:predictive-open` on document when a
     dropdown opens (the mega menu closes on it) and closes on
     `stoklink:mega-open`. Re-initialises on shopify:section:load. */
(function () {
  'use strict';

  if (!window.fetch || !window.DOMParser || !window.AbortController) return;

  var MIN_CHARS = 2;
  var DEBOUNCE = 300;
  var CACHE_MAX = 20;
  var LOADING_DELAY = 400;
  var uid = 0;

  function init(wrapper) {
    if (wrapper.stoklinkPredictiveReady) return;
    var form = wrapper.querySelector('form');
    var input = form ? form.querySelector('input[name="q"]') : null;
    var results = wrapper.querySelector('[data-predictive-results]');
    var live = wrapper.querySelector('[data-predictive-live]');
    if (!form || !input || !results) return;
    wrapper.stoklinkPredictiveReady = true;

    var endpoint = wrapper.getAttribute('data-predictive-url') || '/search/suggest';
    var fallbackUrl = wrapper.getAttribute('data-fallback-url') || form.getAttribute('action') || '/search';
    var sectionId = wrapper.getAttribute('data-section') || 'stoklink-predictive-search';
    var limit = parseInt(wrapper.getAttribute('data-limit'), 10) || 6;
    var unavailable = wrapper.getAttribute('data-unavailable') || 'last';
    var types = wrapper.getAttribute('data-types') || 'product';
    var loadingText = wrapper.getAttribute('data-predictive-loading') || '';

    if (!input.id) input.id = 'stoklink-predictive-' + ++uid;
    var prefix = input.id;
    var listboxId = prefix + '-' + sectionId + '-listbox';
    if (!results.id) results.id = prefix + '-results';

    var fallback = false;
    var pausedUntil = 0;
    var controller = null;
    var debounceTimer = null;
    var loadingTimer = null;
    var cache = {};
    var cacheKeys = [];
    var isOpen = false;
    var options = [];
    var activeIndex = -1;
    var currentQuery = '';
    var pointerInside = false;

    /* Combobox semantics. type=text first: Safari ignores role on type=search. */
    input.type = 'text';
    input.setAttribute('inputmode', 'search');
    input.setAttribute('enterkeyhint', 'search');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listboxId);

    function enc(value) {
      return encodeURIComponent(value);
    }

    function join(url) {
      return url + (url.indexOf('?') === -1 ? '?' : '&');
    }

    function buildUrl(query) {
      if (fallback) {
        return join(fallbackUrl) + 'section_id=' + enc(sectionId) + '&q=' + enc(query) + '&type=product&options[prefix]=last';
      }
      return join(endpoint) + 'q=' + enc(query) +
        '&resources[type]=' + enc(types) +
        '&resources[limit]=' + limit +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=' + enc(unavailable) +
        '&section_id=' + enc(sectionId);
    }

    function remember(query, markup) {
      if (!Object.prototype.hasOwnProperty.call(cache, query)) {
        cacheKeys.push(query);
        if (cacheKeys.length > CACHE_MAX) delete cache[cacheKeys.shift()];
      }
      cache[query] = markup;
    }

    /* Every id in the injected section gets the input id as a prefix, so two
       wrappers on one page (desktop + mobile header, hero) never share ids. */
    function prefixIds(root) {
      var map = {};
      Array.prototype.forEach.call(root.querySelectorAll('[id]'), function (el) {
        var next = prefix + '-' + el.id;
        map[el.id] = next;
        el.id = next;
      });
      ['aria-labelledby', 'aria-controls', 'aria-describedby'].forEach(function (attr) {
        Array.prototype.forEach.call(root.querySelectorAll('[' + attr + ']'), function (el) {
          var tokens = el.getAttribute(attr).split(/\s+/).map(function (token) {
            return map[token] || token;
          });
          el.setAttribute(attr, tokens.join(' '));
        });
      });
    }

    function reveal(option) {
      var list = option.closest('[role="listbox"]');
      if (!list) return;
      var top = option.offsetTop;
      var bottom = top + option.offsetHeight;
      if (top < list.scrollTop) {
        list.scrollTop = top;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    }

    function setActive(index, scroll) {
      activeIndex = index;
      options.forEach(function (option, i) {
        option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      if (index >= 0 && options[index]) {
        input.setAttribute('aria-activedescendant', options[index].id);
        if (scroll !== false) reveal(options[index]);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function announce(text) {
      if (live) live.textContent = text;
    }

    function open(hasListbox) {
      /* aria-expanded is true only while a real listbox is in the DOM; the
         empty and loading panels keep it false so aria-controls stays valid. */
      input.setAttribute('aria-expanded', hasListbox === false ? 'false' : 'true');
      if (isOpen) return;
      isOpen = true;
      results.hidden = false;
      wrapper.classList.add('is-open');
      document.dispatchEvent(new CustomEvent('stoklink:predictive-open', { detail: { wrapper: wrapper } }));
    }

    function close() {
      clearTimeout(debounceTimer);
      clearTimeout(loadingTimer);
      if (controller) {
        controller.abort();
        controller = null;
      }
      wrapper.classList.remove('is-loading');
      if (!isOpen) return;
      isOpen = false;
      results.hidden = true;
      wrapper.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', listboxId);
      setActive(-1);
    }

    function render(markup) {
      results.innerHTML = markup;
      prefixIds(results);
      var listbox = results.querySelector('[role="listbox"]');
      input.setAttribute('aria-controls', listbox ? listbox.id : listboxId);
      options = Array.prototype.slice.call(results.querySelectorAll('[role="option"]'));
      setActive(-1);
      wrapper.classList.remove('is-loading');
      open(!!listbox);
      var status = results.querySelector('[data-predictive-status]');
      announce(status ? status.textContent.replace(/\s+/g, ' ').trim() : '');
    }

    /* Shown only when the first answer takes a while and nothing is open yet. */
    function showLoading() {
      if (isOpen || !loadingText) return;
      var panel = document.createElement('div');
      panel.className = 'predictive__panel';
      var text = document.createElement('p');
      text.className = 'predictive__loading';
      text.textContent = loadingText;
      panel.appendChild(text);
      results.innerHTML = '';
      results.appendChild(panel);
      options = [];
      open(false);
    }

    function load(query) {
      if (Date.now() < pausedUntil) return;
      if (Object.prototype.hasOwnProperty.call(cache, query)) {
        render(cache[query]);
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      var signal = controller.signal;
      var headers = { Accept: 'text/html' };
      wrapper.classList.add('is-loading');
      clearTimeout(loadingTimer);
      loadingTimer = setTimeout(showLoading, LOADING_DELAY);

      fetch(buildUrl(query), { signal: signal, headers: headers })
        .then(function (response) {
          if (response.status === 417 && !fallback) {
            fallback = true;
            return fetch(buildUrl(query), { signal: signal, headers: headers });
          }
          return response;
        })
        .then(function (response) {
          if (response.status === 429) {
            var retry = parseInt(response.headers.get('Retry-After'), 10);
            pausedUntil = Date.now() + (retry > 0 ? retry : 5) * 1000;
            throw new Error('rate-limited');
          }
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then(function (html) {
          if (signal.aborted) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.getElementById('shopify-section-' + sectionId);
          var markup = section ? section.innerHTML.trim() : '';
          if (!markup) throw new Error('empty');
          clearTimeout(loadingTimer);
          remember(query, markup);
          if (input.value.trim() === query) {
            render(markup);
          } else {
            wrapper.classList.remove('is-loading');
          }
        })
        .catch(function (error) {
          if ((error && error.name === 'AbortError') || signal.aborted) return;
          close();
        });
    }

    function queue() {
      var query = input.value.trim();
      clearTimeout(debounceTimer);
      if (query.length < MIN_CHARS) {
        currentQuery = '';
        close();
        return;
      }
      debounceTimer = setTimeout(function () {
        if (query === currentQuery && isOpen) return;
        currentQuery = query;
        load(query);
      }, DEBOUNCE);
    }

    function reopen() {
      if (isOpen) return;
      var query = input.value.trim();
      if (query.length >= MIN_CHARS && Object.prototype.hasOwnProperty.call(cache, query)) {
        currentQuery = query;
        render(cache[query]);
      }
    }

    function openActive() {
      var option = options[activeIndex];
      if (!option) return false;
      var link = option.matches('a[href]') ? option : option.querySelector('a[href]');
      if (!link) return false;
      link.click();
      return true;
    }

    input.addEventListener('input', queue);
    input.addEventListener('focus', reopen);
    input.addEventListener('click', reopen);

    input.addEventListener('keydown', function (event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      switch (event.key) {
        case 'ArrowDown':
        case 'Down':
          if (!isOpen) {
            var query = input.value.trim();
            if (query.length >= MIN_CHARS) {
              event.preventDefault();
              currentQuery = query;
              load(query);
            }
            return;
          }
          if (!options.length) return;
          event.preventDefault();
          setActive(activeIndex + 1 >= options.length ? 0 : activeIndex + 1);
          break;
        case 'ArrowUp':
        case 'Up':
          if (!isOpen || !options.length) return;
          event.preventDefault();
          setActive(activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
          break;
        case 'Enter':
          if (isOpen && activeIndex >= 0 && openActive()) event.preventDefault();
          break;
        case 'Escape':
        case 'Esc':
          if (isOpen) {
            event.preventDefault();
            close();
          }
          break;
        case 'Tab':
          close();
          break;
        default:
      }
    });

    results.addEventListener('click', function (event) {
      if (event.target.closest('[data-predictive-submit]')) {
        event.preventDefault();
        close();
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
        return;
      }
      if (event.target.closest('[data-predictive-close]')) {
        close();
        input.focus();
        return;
      }
      if (!event.target.closest('a[href]')) input.focus();
    });

    results.addEventListener('mousemove', function (event) {
      var option = event.target.closest('[role="option"]');
      if (!option) return;
      var index = options.indexOf(option);
      if (index !== -1 && index !== activeIndex) setActive(index, false);
    });

    /* A pointer press inside the wrapper blurs the input before the click
       lands on a row; do not close in between. */
    wrapper.addEventListener('pointerdown', function () {
      pointerInside = true;
    });
    document.addEventListener('pointerup', function () {
      pointerInside = false;
    });
    document.addEventListener('pointercancel', function () {
      pointerInside = false;
    });

    wrapper.addEventListener('focusout', function (event) {
      if (pointerInside) return;
      if (event.relatedTarget && wrapper.contains(event.relatedTarget)) return;
      /* Deferred: on iOS the pointerup can arrive before the blur. */
      setTimeout(function () {
        if (!pointerInside && !wrapper.contains(document.activeElement)) close();
      }, 0);
    });

    document.addEventListener('click', function (event) {
      if (!isOpen || wrapper.contains(event.target)) return;
      close();
    });

    form.addEventListener('submit', function () {
      close();
    });

    document.addEventListener('stoklink:mega-open', function () {
      close();
    });
    document.addEventListener('stoklink:predictive-open', function (event) {
      if (!event.detail || event.detail.wrapper !== wrapper) close();
    });
  }

  function initAll(scope) {
    if (scope !== document && scope.matches && scope.matches('[data-predictive]')) init(scope);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-predictive]'), init);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
